import React, { useState, useEffect } from 'react'
import { User, UserLevelFormData, UserLevelHistory } from '../../../types'
import { userService } from '../../../services/userService'
import LevelBadge, { getLevelName } from '../../../components/common/LevelBadge'

interface UserLevelModalProps {
  user: User
  onSubmit: (data: UserLevelFormData) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}

const UserLevelModal: React.FC<UserLevelModalProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [formData, setFormData] = useState<UserLevelFormData>({
    newLevel: user.level,
    reason: ''
  })
  const [history, setHistory] = useState<UserLevelHistory[]>([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [showHistory, setShowHistory] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Load level history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        setHistoryLoading(true)
        const levelHistory = await userService.getUserLevelHistory(user.id)
        setHistory(levelHistory)
      } catch (error) {
        console.error('Failed to load level history:', error)
      } finally {
        setHistoryLoading(false)
      }
    }

    if (showHistory) {
      loadHistory()
    }
  }, [user.id, showHistory])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (formData.newLevel < 0 || formData.newLevel > 9) {
      newErrors.newLevel = '職等必須在 0-9 之間'
    }

    if (formData.newLevel === user.level) {
      newErrors.newLevel = '新職等不能與目前職等相同'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    await onSubmit(formData)
  }

  const handleLevelChange = (newLevel: number) => {
    setFormData(prev => ({ ...prev, newLevel }))
    if (errors.newLevel) {
      setErrors(prev => ({ ...prev, newLevel: '' }))
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">職等調整</h2>
          <p className="text-sm text-gray-600 mt-1">
            調整 {user.name} 的職等設定
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Current Level Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">目前職等</h3>
            <div className="flex items-center gap-3">
              <LevelBadge level={user.level} showLabel size="lg" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Level Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                選擇新職等 *
              </label>
              <div className="grid grid-cols-5 gap-3">
                {Array.from({ length: 10 }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleLevelChange(i)}
                    className={`
                      p-3 rounded-lg border-2 transition-all duration-200
                      ${formData.newLevel === i
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                      ${i === user.level ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    disabled={i === user.level}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <LevelBadge level={i} size="sm" />
                      <span className="text-xs text-gray-600 text-center">
                        {getLevelName(i)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              {errors.newLevel && (
                <p className="text-red-600 text-sm mt-2">{errors.newLevel}</p>
              )}
            </div>

            {/* Selected Level Preview */}
            {formData.newLevel !== user.level && (
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-green-800 mb-2">調整預覽</h4>
                <div className="flex items-center gap-3">
                  <LevelBadge level={user.level} showLabel />
                  <span className="text-gray-400">→</span>
                  <LevelBadge level={formData.newLevel} showLabel animated />
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                調整原因
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="請輸入職等調整的原因..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* History Toggle */}
            <div className="pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center text-sm text-primary-600 hover:text-primary-700"
              >
                <svg
                  className={`w-4 h-4 mr-2 transition-transform ${showHistory ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                查看職等變更歷史
              </button>

              {/* History List */}
              {showHistory && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {historyLoading ? (
                    <div className="text-center text-gray-500 py-4">載入中...</div>
                  ) : history.length === 0 ? (
                    <div className="text-center text-gray-500 py-4">暫無職等變更記錄</div>
                  ) : (
                    <div className="space-y-3">
                      {history.map((record) => (
                        <div key={record.id} className="bg-white p-3 rounded border">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {record.oldLevel !== null && record.oldLevel !== undefined && (
                                <LevelBadge level={record.oldLevel} size="sm" />
                              )}
                              {record.oldLevel !== null && record.oldLevel !== undefined && (
                                <span className="text-gray-400 text-xs">→</span>
                              )}
                              <LevelBadge level={record.newLevel} size="sm" />
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(record.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">調整者：</span>
                            {record.changer.name}
                          </div>
                          {record.reason && (
                            <div className="text-xs text-gray-600 mt-1">
                              <span className="font-medium">原因：</span>
                              {record.reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            disabled={isLoading}
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || formData.newLevel === user.level}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            確認調整
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserLevelModal
